import { useEffect, useState } from "react";
import getSymbolFromCurrency from "currency-symbol-map";
import { Options } from "./components/Options/Options";
import { Checkbox } from "./components/Checkbox/Checkbox";
import { Geo } from "./components/Geo/Geo";
import { ActiveLabel } from "./components/ActiveLabel/ActiveLabel";
import {
  ConfiguredColumns,
  KeysOfStringAndNumberProps,
  KeysSortedColumns,
  RenderDataCellType,
  RenderHeaderCellType,
} from "./types";

import { t } from "i18next";
import { formatDate, formatTime, useTimeFormat } from "helpers/timeFormat";
import styles from "./Table.module.scss";
import {
  defaultSortingColumns,
  TDefaultSortingColumns,
} from "./config/sortingColumns";
import { Locale } from "./components/Geo/Locale";
import Tooltip from "components/Tooltip";
import { defaultColumnNames } from "./config/columnNames";
import { Devices } from "./components/Devices/Devices";
import { OnlineLabel } from "./components/OnlineLabel/OnlineLabel";
import NoImage from "assets/images/image_not_found.webp";
import Loader from "components/global/Loader";
import { KindLabel, KindType } from "./components/KindLabel/KindLabel";
import { WagersLabel, WagersType } from "./components/WagersLabel/WagersLabel";
import RiskScoreCell from "./components/RiskScoreCell/RiskScoreCell";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "store/index";
import { LongTextCell } from "./components/LongTextCell/LongTextCell";

type Props<T, K extends KeysOfStringAndNumberProps<T>> = {
  data: T[];
  idField: K;
  order?: { [key in keyof T]?: string };
  columns?: ConfiguredColumns<T>;
  hasCheckbox?: boolean;
  hasOptions?: boolean;
  hasDelete?: boolean;
  hasEdit?: boolean;
  customOptions?: (row: T) => React.ReactElement;
  customColumns?: (row: T) => Partial<Record<keyof T, React.ReactElement>>;
  selectedItems?: Array<T[K]>;
  onSelect?: (id: T[K]) => void;
  onAllSelect?: () => void;
  onEdit?: (id: T[K]) => void;
  onEditByRowClick?: (id: T[K]) => void;
  onDelete?: (id: T[K]) => void;
  onSort?: (key: KeysSortedColumns<T>) => void;
  processingId?: T[K];
  className?: string;
  hasView?: boolean;
  hasInactive?: boolean;
  onInactive?: () => void;
  onView?: (id: T[K]) => void;
  activeRowId?: number | string | null;
  optionsLabel?: string;
};

export function Table<
  T extends Object,
  K extends KeysOfStringAndNumberProps<T>,
>({
  data,
  columns,
  idField,
  order,
  hasCheckbox = true,
  hasOptions = true,
  hasDelete = true,
  hasEdit = true,
  selectedItems = [],
  customOptions,
  customColumns,
  onSelect = () => {},
  onAllSelect = () => {},
  onEdit,
  onEditByRowClick,
  onDelete = () => {},
  onSort = () => {},
  processingId,
  className,
  hasView = false,
  hasInactive = false,
  onInactive = () => {},
  onView,
  activeRowId,
  optionsLabel = "Options",
}: Props<T, K>) {
  const timeFormat = useTimeFormat();
  const location = useLocation();
  const activeModal = useAppSelector((store) => store.modals.activeModal);
  const [clickedRowId, setClickedRowId] = useState<T[K] | null>(null);
  const [activeConfirmId, setActiveConfirmId] = useState<
    string | number | null
  >(null);
  const searchParams = new URLSearchParams(location.search);
  const inferredActiveRowId =
    searchParams.get("entityId") || searchParams.get("documentId");
  const resolvedActiveRowId =
    activeRowId ?? inferredActiveRowId ?? clickedRowId;
  const rowAction =
    onEditByRowClick ??
    (hasView && onView ? onView : hasEdit ? onEdit : undefined);
  const isRowClickable = Boolean(rowAction);

  useEffect(() => {
    if (!activeModal) {
      setClickedRowId(null);
    }
  }, [activeModal]);

  if (!data || !data.length || !data[0]) return null;

  const visibleKeys = (
    columns?.fixedColumns ?? (Object.keys(data[0]) as Array<keyof T>)
  ).filter((k) => !columns?.hiddenColumns?.includes(k));
  const configuredOrder = columns?.columnOrder ?? [];
  const keys = [
    ...configuredOrder.filter((key) => visibleKeys.includes(key)),
    ...visibleKeys.filter((key) => !configuredOrder.includes(key)),
  ];

  //FIX FOR RENDER ALL KEYS
  // KEYS ORDER is followed from biggest object
  // const keys =
  //   data.reduce<(keyof T)[]>((acc, v) => {

  //     if (Object.keys(v).length > acc.length) {
  //       acc.length = 0;
  //       acc.push(...Object.keys(v) as (keyof T)[])
  //     }

  //     return acc;

  //   }, [] as (keyof T)[])
  //   .filter(
  //     (k) => !columns?.hiddenColumns?.includes(k as keyof T),
  //   );

  const columnHeaderToRender = keys.map((k) => ({
    key: k,
    value:
      columns?.sortingColumns?.[k]?.value ??
      defaultColumnNames[k as string] ??
      k ??
      "",

    hasSorting:
      columns?.sortingColumns?.[k]?.hasSorting ??
      defaultSortingColumns.includes(k as TDefaultSortingColumns) ??
      false,
    sortedBy: columns?.sortingColumns?.[k]?.sortedBy ?? order?.[k] ?? "",
    label: columns?.sortingColumns?.[k]?.label ?? "",
    tooltip: columns?.sortingColumns?.[k]?.tooltip ?? "",
  }));

  const renderDataCell = ({
    key,
    value,
    currency,
    selectedRealBalance,
    selectedRealBalanceCurrency,
    onDelete,
    onEdit,
    hasDelete = true,
  }: RenderDataCellType) => {
    switch (key) {
      case "description":
      case "content": {
        return <LongTextCell text={value == null ? "" : String(value)} />;
      }
      case "options": {
        return (
          <Options
            id={value as string | number}
            onEdit={onEdit}
            onDelete={onDelete}
            hasDelete={hasDelete}
            hasEdit={hasEdit}
            activeConfirmId={activeConfirmId}
            setActiveConfirmId={setActiveConfirmId}
            hasView={hasView}
            hasInactive={hasInactive}
            onInactive={onInactive}
            onView={
              onView
                ? (id) => {
                    setClickedRowId(id as T[K]);
                    onView(id as T[K]);
                  }
                : undefined
            }
            isViewActive={
              resolvedActiveRowId !== null &&
              resolvedActiveRowId !== undefined &&
              String(resolvedActiveRowId) === String(value)
            }
          />
        );
      }

      case "isActive": {
        return <ActiveLabel isActive={value as boolean} />;
      }

      case "geo": {
        return <Geo geo={value as string[]} />;
      }

      case "currency":
      case "currencies": {
        const currencies = Array.isArray(value)
          ? value
          : typeof value === "string"
            ? value.split(",")
            : [];
        const normalizedCurrencies = currencies
          .map((currency) => currency.trim())
          .filter(Boolean);

        if (normalizedCurrencies.length === 0) return <>-</>;

        const currencyItem = (currency: string) => (
          <div key={currency} className="currency-item">
            {getSymbolFromCurrency(currency)} {currency}
          </div>
        );

        return (
          <div className="currency-row">
            {normalizedCurrencies.map((currency) => currencyItem(currency))}
          </div>
        );
      }

      case "translations":
      case "locales": {
        const locales = value as string[];

        return <Locale locales={locales} />;
      }

      case "provider": {
        type Provider = {
          id: number;
          title: string;
          slug: string;
          image: string;
          countOfGames: number;
          isActive: boolean;
        };

        const provider = value as Provider;

        return <>{provider.title}</>;
      }

      case "category": {
        type Category = {
          id: number;
          title: string;
          slug: string;
          description: string;
          isActive: false;
          image: string;
          countOfCollections: number;
        };

        const category = value as Category;

        if (category instanceof Object) {
          return <>{category.title}</>;
        }

        return <>{category}</>;
      }

      case "created":
      case "completed":
      case "effectiveFrom":
      case "lastUpdated":
      case "registeredAt":
      case "updatedAt": {
        const rawValue = value as string;

        if (
          key === "effectiveFrom" &&
          (rawValue == null ||
            rawValue === "" ||
            Number(rawValue) === 0 ||
            Date.parse(rawValue) === 0)
        ) {
          return null;
        }

        const date = formatDate(rawValue);
        const time = formatTime(rawValue, timeFormat);

        if (date === rawValue && time === rawValue) return <>{rawValue}</>;

        return (
          <>
            <span>{date}</span>
            <span>{time}</span>
          </>
        );
      }

      case "balanceReal": {
        if (selectedRealBalance != null && selectedRealBalanceCurrency) {
          const symbol = getSymbolFromCurrency(selectedRealBalanceCurrency);
          const prefix = symbol
            ? `${symbol} `
            : `${selectedRealBalanceCurrency} `;
          return (
            <>
              {prefix}
              {selectedRealBalance}
            </>
          );
        }
        const accountValue = value as number;
        const accountSymbol = getSymbolFromCurrency(currency ?? "") ?? "$";
        return (
          <>
            {accountSymbol} {accountValue?.toFixed(2)}
          </>
        );
      }
      case "balance":
      case "balanceBonus": {
        const val = value as number;
        const symbol = getSymbolFromCurrency(currency ?? "") ?? "$";

        return (
          <>
            {symbol} {val?.toFixed(2)}
          </>
        );
      }

      case "verificationStatus": {
        const status = (value as string)?.toLowerCase() || "none";

        return <span className={`badge badge--${status}`}>{status}</span>;
      }

      case "isOnline": {
        const online = value as boolean;

        return <OnlineLabel isOnline={online} />;
      }

      case "devices": {
        const devices = value as string[];

        return <Devices devices={devices} />;
      }

      case "priority": {
        const priority = value as "low" | "medium" | "high";

        return <span className={`badge badge--${priority}`}>{priority}</span>;
      }

      case "statusCode":
      case "status": {
        const status = (value as string)?.toLowerCase();

        return (
          <span className={`badge badge--${status}`}>{value as string}</span>
        );
      }
      case "wagerBalances":
        const wagers = value as WagersType[];
        return <WagersLabel wagers={wagers} />;

      case "currencyCoverage": {
        if (!value || value === "-") return "-";

        const statusMap: Record<string, string> = {
          READY: "✅",
          OK: "✅",
          PARTIAL: "⚠️",
          BROKEN: "❌",
        };

        const items = (value as string).split("|").map((element) => {
          const [key, status] = element.split(":");
          const newKey = key?.trim() || "-";
          const newStatus = status?.trim() || "-";
          return `${newKey}: ${statusMap[newStatus] || newStatus}`;
        });

        return (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, max-content)",
              columnGap: 8,
              rowGap: 6,
            }}
          >
            {items.map((el, i) => (
              <span key={i}>{el}</span>
            ))}
          </div>
        );
      }

      case "activationReadiness": {
        const statusMap: Record<string, string> = {
          READY: "✅",
          OK: "✅",
          PARTIAL: "⚠️",
          BROKEN: "❌",
        };

        if (!value || (value as string).trim() === "") return "-";

        const items = (value as string).split("|").map((element) => {
          const [key, status] = element.split(":");
          const newKey = key?.trim() || "-";
          const newStatus = status?.trim() || "-";
          return `${newKey}: ${statusMap[newStatus] || newStatus}`;
        });

        return (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, max-content)",
              columnGap: "8px",
              rowGap: "6px",
            }}
          >
            {items.map((el, i) => (
              <span key={i}>{el}</span>
            ))}
          </div>
        );
      }

      case "kind": {
        const kinds = value as KindType[];
        return <KindLabel kinds={kinds} />;
      }

      case "paymentProvideImage":
      case "paymentProviderImage":
      case "imageSmall":
      case "icon":
      case "image": {
        const fit = columns?.image?.imageFit ?? "cover";

        return (
          <img
            src={(value as string) || NoImage}
            alt="No image"
            style={{
              objectFit: fit,
              objectPosition: "center",
              borderRadius: "4px",
            }}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = NoImage;
            }}
          />
        );
      }

      case "riskScore": {
        return (
          <RiskScoreCell
            riskScore={
              value as {
                value: number | null;
                type: "low" | "medium" | "high" | "none";
              }
            }
          />
        );
      }

      default:
        return <>{value?.toString()}</>;
    }
  };

  const renderHeaderCell = ({
    key,
    value,
    hasSorting,
    onClick,
    sortedBy,
    label = "",
    tooltip = "",
  }: RenderHeaderCellType<T>) => {
    if (hasSorting || label || tooltip) {
      return (
        <div className={styles.withOrder}>
          {hasSorting && (
            <div
              className={`${styles.order} ${styles[sortedBy as string]}`}
              onClick={() => {
                if (onClick && key) {
                  onClick(key);
                }
              }}
            >
              <span>&uarr;</span>
              <span>&darr;</span>
            </div>
          )}
          {value}
          {label && <span className={styles["thead-label"]}>{label}</span>}
          {tooltip && <Tooltip text={tooltip} position="right" />}
        </div>
      );
    }

    return <>{value}</>;
  };

  const handleClickRow = (
    e: React.MouseEvent<HTMLTableRowElement>,
    id?: number | string,
  ) => {
    e.stopPropagation();

    if (rowAction && id !== undefined) {
      setClickedRowId(id as T[K]);
      rowAction(id as T[K]);
    }
  };

  return (
    <table className={`${styles.table} ${className || ""}`}>
      <thead className={styles.thead}>
        <tr>
          {hasCheckbox && (
            <th>
              <Checkbox
                onClick={onAllSelect}
                checked={selectedItems.length === data.length}
              />
            </th>
          )}

          {columnHeaderToRender.map((h) => (
            <th key={h?.key as string}>
              {renderHeaderCell({
                key: h?.key,
                value: t(h.value as string),
                hasSorting: h.hasSorting,
                onClick: (k) => onSort(k as KeysSortedColumns<T, keyof T>),
                sortedBy: h.sortedBy,
                label: h?.label,
                tooltip: h?.tooltip,
              })}
            </th>
          ))}

          {hasOptions && <th>{renderHeaderCell({ value: optionsLabel })}</th>}
        </tr>
      </thead>
      <tbody>
        {data.map((row) => (
          <tr
            key={String(row[idField])}
            onClick={(e) => handleClickRow(e, row[idField] as number)}
            className={`
  ${processingId === row[idField] ? styles["processing-row"] : ""}
  ${activeConfirmId === row[idField] ? styles["row-confirm-active"] : ""}
  ${isRowClickable ? styles["clickable-row"] : ""}
  ${
    resolvedActiveRowId !== null &&
    resolvedActiveRowId !== undefined &&
    String(resolvedActiveRowId) === String(row[idField])
      ? styles["row-rate-active"]
      : ""
  }
`}
          >
            {hasCheckbox && (
              <td>
                <Checkbox
                  id={row[idField]}
                  checked={selectedItems.includes(row[idField])}
                  onClick={() => onSelect(row[idField])}
                />
              </td>
            )}

            {keys.map((k) => {
              const cellClassName = `${k === "description" || k === "content" ? styles["big-text"] : ""} ${["created", "completed", "effectiveFrom", "lastUpdated", "registeredAt", "updatedAt"].includes(String(k)) ? styles.date : ""}`;

              if (customColumns) {
                const customRenderedColumnsKeys = Object.keys(
                  customColumns(row),
                ) as Array<keyof T>;

                if (customRenderedColumnsKeys.includes(k)) {
                  return (
                    <td key={String(k)} className={cellClassName}>
                      {customColumns(row)[k]}
                    </td>
                  );
                }
              }

              return (
                <td key={String(k)} className={cellClassName}>
                  {renderDataCell({
                    key: k as string,
                    value: row[k] as any,
                    currency:
                      typeof (row as Record<string, unknown>).currency ===
                      "string"
                        ? ((row as Record<string, unknown>).currency as string)
                        : undefined,
                    selectedRealBalance:
                      typeof (row as Record<string, unknown>).selectedRealBalance ===
                      "string"
                        ? ((row as Record<string, unknown>)
                            .selectedRealBalance as string)
                        : undefined,
                    selectedRealBalanceCurrency:
                      typeof (row as Record<string, unknown>)
                        .selectedRealBalanceCurrency === "string"
                        ? ((row as Record<string, unknown>)
                            .selectedRealBalanceCurrency as string)
                        : undefined,
                  })}
                </td>
              );
            })}

            {hasOptions && !customOptions && (
              <td onClick={(e) => e.stopPropagation()}>
                {renderDataCell({
                  key: "options",
                  value: row[idField],
                  onEdit: onEdit
                    ? (id) => {
                        setClickedRowId(id as T[K]);
                        onEdit(id as T[K]);
                      }
                    : undefined,
                  onDelete: (id) => onDelete(id as T[K]),
                  hasDelete: hasDelete,
                })}
              </td>
            )}

            {customOptions && (
              <td onClick={(e) => e.stopPropagation()}>{customOptions(row)}</td>
            )}

            {processingId === row[idField] && (
              <div className={styles["processing-spinner"]}>
                <Loader />
              </div>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
